import { useEffect, useRef, type ReactNode, type RefObject } from 'react';

interface PopoverProps {
    open: boolean;
    onClose(): void;
    // The button that opens it; a click on that is not an outside click.
    anchorRef: RefObject<HTMLElement | null>;
    label: string;
    className?: string;
    children: ReactNode;
}

// A small panel anchored under its button. Closes on Escape and on a click or
// tap outside, and hands focus back to the button.
export function Popover({ open, onClose, anchorRef, label, className, children }: PopoverProps) {
    const panelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) {
            return;
        }
        const anchor = anchorRef.current;
        const onPointerDown = (event: PointerEvent) => {
            const target = event.target as Node;
            if (!panelRef.current?.contains(target) && !anchor?.contains(target)) {
                onClose();
            }
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
                anchor?.focus();
            }
        };
        document.addEventListener('pointerdown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open, onClose, anchorRef]);

    if (!open) {
        return null;
    }
    return (
        <div ref={panelRef} role="dialog" aria-label={label} className={['popover', className].filter(Boolean).join(' ')}>
            {children}
        </div>
    );
}
